/**
 * Lightweight CPP wizard config — data-driven step definitions.
 *
 * Phase 20 (Refactor 3, supplemented in Phase 20-02). Mirrors
 * `LIGHTWEIGHT_CPP_WIZARD_STEPS` (../wizard-schemas/lightweight-cpp.ts).
 * 5 content steps + preview.
 *
 * Step renderer assignment:
 *   - `basics`    → custom (`LightweightCppBasicsBuilder` — the
 *                   domestic-confirmation card UX needs a richer
 *                   layout than the generic toggle primitive draws)
 *   - `hazards`   → data-driven (textarea + AI)
 *   - `controls`  → data-driven (textarea + AI)
 *   - `welfare`   → data-driven (textarea + AI)
 *   - `emergency` → data-driven (2 textareas + AI)
 *   - `preview`   → custom (`LightweightCppPreview`)
 *
 * AI section-fill enabled across the content steps — the lightweight
 * CPP dispatcher has been wired since Phase 14b.
 */
import type { WizardConfig } from "./types";

export const LIGHTWEIGHT_CPP_WIZARD_CONFIG: WizardConfig = {
  documentType: "CPP_LIGHTWEIGHT",
  variant: "LIGHTWEIGHT",
  steps: [
    {
      id: "basics",
      titleKey: "wizards.cppLightweight.stepTitles.basics",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.basics",
      schemaRef: "CPP_LIGHTWEIGHT.steps.basics",
      customRenderer: "LightweightCppBasicsBuilder",
    },
    {
      id: "hazards",
      titleKey: "wizards.cppLightweight.stepTitles.hazards",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.hazards",
      schemaRef: "CPP_LIGHTWEIGHT.steps.hazards",
      fields: [
        {
          id: "siteRisks",
          type: "textarea",
          labelKey: "wizards.cppLightweight.fields.siteRisks",
          placeholderKey: "wizards.cppLightweight.placeholders.siteRisks",
          required: true,
          rows: 5,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "CPP_LIGHTWEIGHT",
        sectionKey: "siteRisks",
        targetFieldId: "siteRisks",
      },
    },
    {
      id: "controls",
      titleKey: "wizards.cppLightweight.stepTitles.controls",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.controls",
      schemaRef: "CPP_LIGHTWEIGHT.steps.controls",
      fields: [
        {
          id: "controlMeasures",
          type: "textarea",
          labelKey: "wizards.cppLightweight.fields.controlMeasures",
          placeholderKey: "wizards.cppLightweight.placeholders.controlMeasures",
          required: true,
          rows: 5,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "CPP_LIGHTWEIGHT",
        sectionKey: "controlMeasures",
        targetFieldId: "controlMeasures",
      },
    },
    {
      id: "welfare",
      titleKey: "wizards.cppLightweight.stepTitles.welfare",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.welfare",
      schemaRef: "CPP_LIGHTWEIGHT.steps.welfare",
      fields: [
        {
          id: "welfareArrangements",
          type: "textarea",
          labelKey: "wizards.cppLightweight.fields.welfareArrangements",
          placeholderKey:
            "wizards.cppLightweight.placeholders.welfareArrangements",
          required: true,
          rows: 4,
        },
      ],
    },
    {
      id: "emergency",
      titleKey: "wizards.cppLightweight.stepTitles.emergency",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.emergency",
      schemaRef: "CPP_LIGHTWEIGHT.steps.emergency",
      fields: [
        {
          id: "fireAndFirstAid",
          type: "textarea",
          labelKey: "wizards.cppLightweight.fields.fireAndFirstAid",
          placeholderKey:
            "wizards.cppLightweight.placeholders.fireAndFirstAid",
          required: true,
          rows: 4,
        },
        {
          id: "nearestHospital",
          type: "text",
          labelKey: "wizards.cppLightweight.fields.nearestHospital",
          helperKey: "wizards.cppLightweight.helpers.nearestHospital",
          required: true,
        },
      ],
      aiSectionSuggest: {
        enabled: true,
        documentType: "CPP_LIGHTWEIGHT",
        sectionKey: "fireAndFirstAid",
        targetFieldId: "fireAndFirstAid",
      },
    },
    {
      id: "preview",
      titleKey: "wizards.cppLightweight.stepTitles.preview",
      descriptionKey: "wizards.cppLightweight.stepDescriptions.preview",
      schemaRef: "CPP_LIGHTWEIGHT.steps.preview",
      customRenderer: "LightweightCppPreview",
    },
  ],
};
